import { useEffect, useRef, useState } from 'react';

import type { CampoDeJogador, Jogador, NovoLider, ValorDeJogador } from '../api/jogadores';

const FOCAVEIS = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const SITUACAO = { ativo: 'Ativo', inativo: 'Inativo', bloqueado: 'Bloqueado' } as const;

interface Rascunho {
    grupo: string;
    lider: boolean;
    situacao: 'ativo' | 'inativo';
}

export interface Alteracao {
    campo: CampoDeJogador;
    valor: ValorDeJogador;
    valorAnterior: ValorDeJogador;
    novoLider: NovoLider | null;
}

interface Props {
    jogador: Jogador;
    turmaId: string;
    grupos: string[];
    /** Os outros participantes do grupo: quem pode assumir a liderança e quem a perde. */
    companheiros: Jogador[];
    aoFechar: () => void;
    /** Rejeita com o erro HTTP; o modal mostra a mensagem e continua aberto. */
    aoSalvar: (alteracao: Alteracao) => Promise<void>;
}

function mensagemDoErro(erro: unknown): string {
    const corpo = (erro as { corpo?: { mensagem?: string } }).corpo;
    if (corpo?.mensagem) return corpo.mensagem;
    const status = (erro as { status?: number }).status;
    if (status === 403) return 'Sessão sem permissão para esta alteração.';
    return 'Não foi possível salvar. Tente novamente.';
}

export function EdicaoDoJogador({
    jogador,
    turmaId,
    grupos,
    companheiros,
    aoFechar,
    aoSalvar,
}: Props) {
    const modal = useRef<HTMLDivElement>(null);
    const [rascunho, setRascunho] = useState<Rascunho>({
        grupo: jogador.grupo_id ?? '',
        lider: jogador.lider,
        // `bloqueado` não é destino: o select mostra o estado atual e exige a escolha.
        situacao: jogador.situacao === 'bloqueado' ? 'ativo' : jogador.situacao,
    });
    const [substituto, setSubstituto] = useState({ login: '', aleatorio: false });
    const [salvando, setSalvando] = useState<CampoDeJogador | null>(null);
    const [aviso, setAviso] = useState<{ campo: CampoDeJogador; texto: string; erro: boolean } | null>(
        null,
    );

    useEffect(() => {
        modal.current?.focus();
        const noTeclado = (evento: KeyboardEvent) => {
            if (evento.key === 'Escape') {
                aoFechar();
                return;
            }
            if (evento.key !== 'Tab' || !modal.current) return;
            // Prende o foco: sem isto o Tab vai mexer na tabela que ficou atrás.
            const focaveis = [...modal.current.querySelectorAll<HTMLElement>(FOCAVEIS)].filter(
                (elemento) => !elemento.hasAttribute('disabled'),
            );
            const primeiro = focaveis[0] ?? modal.current;
            const ultimo = focaveis.at(-1) ?? modal.current;
            const atual = document.activeElement;
            if (evento.shiftKey && (atual === primeiro || atual === modal.current)) {
                evento.preventDefault();
                ultimo.focus();
            } else if (!evento.shiftKey && atual === ultimo) {
                evento.preventDefault();
                primeiro.focus();
            }
        };
        document.addEventListener('keydown', noTeclado);
        return () => document.removeEventListener('keydown', noTeclado);
    }, [aoFechar]);

    const grupoMudou = rascunho.grupo !== (jogador.grupo_id ?? '');
    const liderAtualDoGrupo = companheiros.find((outro) => outro.lider) ?? null;
    // Quem perde a liderança tem o substituto sorteado pelo servidor entre os ativos; informar a
    // matrícula (ou marcar "aleatório") é opcional e nunca exigido (`RN-RA-045`, `DP-RA-051 c`).
    const perdeLideranca = jogador.lider && (grupoMudou || !rascunho.lider);
    const precisaSubstituto = perdeLideranca && companheiros.length > 0;

    const pacoteDoSubstituto = (): NovoLider | null => {
        if (!precisaSubstituto) return null;
        if (substituto.aleatorio) return { login: '', aleatorio: true };
        const matricula = substituto.login.trim();
        return matricula ? { login: matricula, aleatorio: false } : null;
    };

    async function salvar(campo: CampoDeJogador, pergunta: string, alteracao: Omit<Alteracao, 'novoLider'>) {
        const novoLider = pacoteDoSubstituto();
        if (novoLider && !novoLider.aleatorio) {
            const matricula = novoLider.login;
            if (!companheiros.some((outro) => outro.login.toLowerCase() === matricula.toLowerCase())) {
                setAviso({
                    campo,
                    texto: `${matricula} não é participante do ${jogador.grupo_id}.`,
                    erro: true,
                });
                return;
            }
        }
        if (!window.confirm(pergunta)) return;

        setAviso(null);
        setSalvando(campo);
        try {
            await aoSalvar({ ...alteracao, novoLider });
            setAviso({ campo, texto: 'Salvo.', erro: false });
            setSubstituto({ login: '', aleatorio: false });
        } catch (erro) {
            setAviso({ campo, texto: mensagemDoErro(erro), erro: true });
        } finally {
            setSalvando(null);
        }
    }

    const haOutroAtivo = companheiros.some((outro) => outro.situacao === 'ativo');

    /** O que o servidor fará com a liderança; sem outro ativo não há quem sortear (`DP-RA-050`). */
    const efeitoNoSubstituto = (desfazendo: boolean) => {
        if (!perdeLideranca) return '';
        if (!haOutroAtivo) {
            const aviso = ` Não há outro participante ativo no ${jogador.grupo_id} para assumir a liderança.`;
            return desfazendo ? `${aviso} O servidor vai recusar.` : aviso;
        }
        return substituto.aleatorio || !substituto.login.trim()
            ? ` O novo líder do ${jogador.grupo_id} será sorteado entre os ativos.`
            : ` ${substituto.login.trim()} assume a liderança do ${jogador.grupo_id}.`;
    };

    const Recado = ({ campo }: { campo: CampoDeJogador }) =>
        aviso?.campo === campo ? (
            <p className={`recado-campo${aviso.erro ? ' erro' : ''}`} role="status">
                {aviso.texto}
            </p>
        ) : null;

    return (
        <div className="fundo-da-carta" data-testid="fundo-da-edicao" onClick={aoFechar}>
            <div
                ref={modal}
                className="edicao-jogador"
                role="dialog"
                aria-modal="true"
                aria-label={`Editar ${jogador.login}`}
                tabIndex={-1}
                onClick={(evento) => evento.stopPropagation()}
            >
                <button type="button" className="fechar-carta" onClick={aoFechar} aria-label="Fechar">
                    ×
                </button>
                <header>
                    <h3>{jogador.login}</h3>
                    <p className="local-do-jogador">
                        {turmaId} · {jogador.grupo_id ?? 'sem grupo'}
                        {jogador.lider && <span className="selo-lider">Líder</span>}
                    </p>
                </header>

                <div className="campo-edicao">
                    <label>
                        Grupo
                        <select
                            value={rascunho.grupo}
                            disabled={salvando !== null}
                            onChange={(evento) =>
                                setRascunho({ ...rascunho, grupo: evento.target.value })
                            }
                        >
                            {grupos.map((grupo) => (
                                <option key={grupo} value={grupo}>
                                    {grupo}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="button"
                        className="botao-sair"
                        disabled={!grupoMudou || salvando !== null}
                        onClick={() =>
                            salvar(
                                'grupo_id',
                                `Mover ${jogador.login} do ${jogador.grupo_id} para o ${rascunho.grupo}?` +
                                    (jogador.sessao_ativa ? ' A sessão dele será encerrada.' : '') +
                                    efeitoNoSubstituto(false),
                                {
                                    campo: 'grupo_id',
                                    valor: rascunho.grupo,
                                    valorAnterior: jogador.grupo_id,
                                },
                            )
                        }
                    >
                        Salvar grupo
                    </button>
                    <Recado campo="grupo_id" />
                </div>

                <div className="campo-edicao">
                    <label className="caixa-marcavel">
                        <input
                            type="checkbox"
                            checked={rascunho.lider}
                            disabled={salvando !== null}
                            onChange={(evento) =>
                                setRascunho({ ...rascunho, lider: evento.target.checked })
                            }
                        />
                        Líder do grupo
                    </label>
                    <button
                        type="button"
                        className="botao-sair"
                        disabled={rascunho.lider === jogador.lider || salvando !== null}
                        onClick={() =>
                            salvar(
                                'lider',
                                rascunho.lider
                                    ? `Tornar ${jogador.login} líder do ${jogador.grupo_id}?` +
                                          (liderAtualDoGrupo
                                              ? ` ${liderAtualDoGrupo.login} deixa de ser líder.`
                                              : '')
                                    : `Tirar a liderança de ${jogador.login} no ${jogador.grupo_id}?` +
                                          efeitoNoSubstituto(true),
                                { campo: 'lider', valor: rascunho.lider, valorAnterior: jogador.lider },
                            )
                        }
                    >
                        Salvar líder
                    </button>
                    <Recado campo="lider" />
                </div>

                {precisaSubstituto && (
                    <fieldset className="novo-lider">
                        <legend>Novo líder do {jogador.grupo_id}</legend>
                        <label>
                            Matrícula do novo líder
                            <input
                                type="text"
                                value={substituto.login}
                                disabled={substituto.aleatorio || salvando !== null}
                                onChange={(evento) =>
                                    setSubstituto({ ...substituto, login: evento.target.value })
                                }
                            />
                        </label>
                        <label className="caixa-marcavel">
                            <input
                                type="checkbox"
                                checked={substituto.aleatorio}
                                disabled={salvando !== null}
                                onChange={(evento) =>
                                    setSubstituto({ login: '', aleatorio: evento.target.checked })
                                }
                            />
                            Líder aleatório
                        </label>
                    </fieldset>
                )}

                <div className="campo-edicao">
                    <label>
                        Situação
                        <select
                            value={rascunho.situacao}
                            disabled={salvando !== null}
                            onChange={(evento) =>
                                setRascunho({
                                    ...rascunho,
                                    situacao: evento.target.value as Rascunho['situacao'],
                                })
                            }
                        >
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </label>
                    <button
                        type="button"
                        className="botao-sair"
                        disabled={rascunho.situacao === jogador.situacao || salvando !== null}
                        onClick={() =>
                            salvar(
                                'situacao',
                                `Mudar a situação de ${jogador.login} de «${SITUACAO[jogador.situacao]}»` +
                                    ` para «${SITUACAO[rascunho.situacao]}»?` +
                                    (jogador.situacao === 'bloqueado' && rascunho.situacao === 'ativo'
                                        ? ' Isso limpa o bloqueio por tentativas de login.'
                                        : '') +
                                    (rascunho.situacao === 'inativo'
                                        ? ' Ele não conseguirá entrar de novo no jogo.'
                                        : ''),
                                {
                                    campo: 'situacao',
                                    valor: rascunho.situacao,
                                    valorAnterior: jogador.situacao,
                                },
                            )
                        }
                    >
                        Salvar situação
                    </button>
                    {jogador.situacao === 'bloqueado' && (
                        <p className="recado-campo">
                            Bloqueado por tentativas de login. Salvar como Ativo desbloqueia.
                        </p>
                    )}
                    <Recado campo="situacao" />
                </div>

                <div className="campo-edicao">
                    <p className="valor-derivado">
                        Tablet vinculado{' '}
                        {jogador.tablet ? <code>{jogador.tablet}</code> : <span className="sem-tablet">nenhum</span>}
                    </p>
                    <button
                        type="button"
                        className="botao-sair"
                        disabled={!jogador.sessao_ativa || salvando !== null}
                        onClick={() =>
                            salvar(
                                'tablet',
                                `Desvincular ${jogador.login} do ${jogador.tablet}? A sessão será` +
                                    ' encerrada e ele poderá entrar em outro tablet.',
                                { campo: 'tablet', valor: null, valorAnterior: jogador.tablet },
                            )
                        }
                    >
                        Desvincular
                    </button>
                    <Recado campo="tablet" />
                </div>
            </div>
        </div>
    );
}
